/**
 * The shape `readAcceptedHtml` needs from a node. `HTMLElement` satisfies it structurally, so call
 * sites pass a ref's element directly — the narrower type is what lets this be tested without a DOM
 * environment, which the repo's vitest projects do not provide.
 */
export interface ConsentNode {
	hasAttribute(name: string): boolean;
	querySelector(selectors: string): unknown;
	innerHTML: string;
}

/** Marks a node whose content has not arrived yet. Put it on the placeholder that renders instead. */
export const CONSENT_PENDING_ATTRIBUTE = 'data-consent-pending';

/**
 * Reads the document a consent record has to store: the text the person actually had in front of
 * them when they ticked the box.
 *
 * Capture sites take a node's `innerHTML` at tick time, and a node whose content arrives from a
 * query is a placeholder until it lands — so without this the record stores skeletons as the
 * accepted contract, and a re-capture (NGO names resolving, a persisted store rehydrating)
 * replaces a real document with them. `undefined` means "nothing worth recording yet": callers keep
 * whatever they already stored rather than overwrite it.
 */
export function readAcceptedHtml(node: ConsentNode | null | undefined): string | undefined {
	if (!node) return undefined;
	if (node.hasAttribute(CONSENT_PENDING_ATTRIBUTE)) return undefined;
	if (node.querySelector(`[${CONSENT_PENDING_ATTRIBUTE}]`) !== null) return undefined;

	const html = node.innerHTML.trim();
	return html === '' ? undefined : html;
}
